import React, { useState, useMemo } from "react";

const StarRating = ({ rating, size = "text-base" }) => {
  return (
    <div className={`flex items-center gap-0.5 text-yellow-400 ${size}`}>
      {[1, 2, 3, 4, 5].map((star) => (
        <span key={star}>{star <= rating ? "★" : "☆"}</span>
      ))}
    </div>
  );
};

const AdminReviews = ({ appointments = [], onOpenDetails }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [starFilter, setStarFilter] = useState("all"); // "all", "5", "4", "3", "2", "1"
  const [serviceFilter, setServiceFilter] = useState("all"); // "all", "grooming", "hotel", "both"
  const [sortBy, setSortBy] = useState("newest"); // "newest", "oldest", "highest", "lowest"

  // Filter completed appointments that have been rated
  const allReviews = useMemo(() => {
    return appointments.filter(
      (a) => a.rating !== undefined && a.rating !== null && Number(a.rating) >= 1
    );
  }, [appointments]);

  const completedAppointments = useMemo(() => {
    return appointments.filter((a) => a.status === "completed");
  }, [appointments]);

  // Review statistics calculation
  const stats = useMemo(() => {
    const total = allReviews.length;
    if (total === 0) {
      return {
        avg: 0,
        total: 0,
        distribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
        percentages: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
        reviewRate: 0,
      };
    }

    const sum = allReviews.reduce((acc, r) => acc + Number(r.rating || 0), 0);
    const avg = (sum / total).toFixed(1);

    const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    allReviews.forEach((r) => {
      const star = Math.min(5, Math.max(1, Math.round(Number(r.rating))));
      distribution[star] = (distribution[star] || 0) + 1;
    });

    const percentages = {};
    for (let s = 1; s <= 5; s++) {
      percentages[s] = Math.round((distribution[s] / total) * 100);
    }

    const reviewRate =
      completedAppointments.length > 0
        ? Math.round((total / completedAppointments.length) * 100)
        : 0;

    return { avg, total, distribution, percentages, reviewRate };
  }, [allReviews, completedAppointments]);

  // Filtered & sorted reviews
  const filteredReviews = useMemo(() => {
    return allReviews
      .filter((apt) => {
        // Star filter
        if (starFilter !== "all" && Number(apt.rating) !== Number(starFilter)) {
          return false;
        }

        // Service filter
        if (serviceFilter !== "all") {
          const hasService = apt.pets?.some((p) => {
            if (serviceFilter === "grooming") return p.serviceType === "grooming" || p.serviceType === "both";
            if (serviceFilter === "hotel") return p.serviceType === "hotel" || p.serviceType === "both";
            if (serviceFilter === "both") return p.serviceType === "both";
            return false;
          });
          if (!hasService) return false;
        }

        // Search term
        if (searchTerm.trim()) {
          const q = searchTerm.toLowerCase();
          const matchOwner = apt.ownerName?.toLowerCase().includes(q);
          const matchEmail = apt.ownerEmail?.toLowerCase().includes(q);
          const matchPhone = apt.ownerPhone?.includes(q);
          const matchFeedback = apt.feedback?.toLowerCase().includes(q);
          const matchPet = apt.pets?.some((p) => p.petName?.toLowerCase().includes(q) || p.pet?.breed?.toLowerCase().includes(q));
          return matchOwner || matchEmail || matchPhone || matchFeedback || matchPet;
        }

        return true;
      })
      .sort((a, b) => {
        const dateA = new Date(a.reviewedAt || a.updatedAt || a.createdAt);
        const dateB = new Date(b.reviewedAt || b.updatedAt || b.createdAt);

        if (sortBy === "newest") return dateB - dateA;
        if (sortBy === "oldest") return dateA - dateB;
        if (sortBy === "highest") return (b.rating || 0) - (a.rating || 0);
        if (sortBy === "lowest") return (a.rating || 0) - (b.rating || 0);
        return 0;
      });
  }, [allReviews, starFilter, serviceFilter, searchTerm, sortBy]);

  return (
    <div className="space-y-8 animate-fade-in">
      {/* ── Top Header & Statistics Cards ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Card 1: Overall Average Rating */}
        <div className="bg-gradient-to-br from-amber-500 via-yellow-500 to-amber-600 rounded-3xl p-6 sm:p-7 text-white shadow-lg flex flex-col justify-between relative overflow-hidden">
          <div className="absolute -top-6 -right-6 w-32 h-32 bg-white/10 rounded-full blur-xl pointer-events-none" />
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-black uppercase tracking-widest text-amber-100">
                ⭐ Overall Customer Score
              </span>
              <span className="px-2.5 py-0.5 rounded-full bg-white/20 text-white text-[10px] font-black uppercase tracking-wide">
                All Time
              </span>
            </div>
            <div className="flex items-baseline gap-3 my-2">
              <span className="text-5xl sm:text-6xl font-black tracking-tight">
                {stats.avg || "0.0"}
              </span>
              <span className="text-xl sm:text-2xl font-bold text-amber-200">/ 5.0</span>
            </div>
            <div className="flex items-center gap-2 mb-2">
              <StarRating rating={Math.round(Number(stats.avg || 0))} size="text-2xl text-yellow-200" />
            </div>
          </div>
          <div className="pt-4 border-t border-white/20 flex justify-between items-center text-xs text-amber-100 font-semibold">
            <span>{stats.total} total customer review{stats.total !== 1 ? "s" : ""}</span>
            <span>{stats.reviewRate}% response rate</span>
          </div>
        </div>

        {/* Card 2: Star Distribution Bar Breakdown */}
        <div className="bg-white rounded-3xl border border-[#e1ebf0] p-6 shadow-sm space-y-2.5 flex flex-col justify-center">
          <p className="text-xs font-black text-gray-400 uppercase tracking-wider mb-1">
            Rating Distribution
          </p>
          {[5, 4, 3, 2, 1].map((stars) => {
            const count = stats.distribution[stars] || 0;
            const pct = stats.percentages[stars] || 0;
            return (
              <div
                key={stars}
                onClick={() => setStarFilter(starFilter === String(stars) ? "all" : String(stars))}
                className={`flex items-center gap-3 text-xs cursor-pointer p-1 rounded-xl transition-colors ${
                  starFilter === String(stars) ? "bg-amber-50 font-bold" : "hover:bg-gray-50"
                }`}
                title={`Filter by ${stars} stars`}
              >
                <div className="flex items-center gap-1 w-12 font-bold text-gray-700">
                  <span>{stars}</span>
                  <span className="text-yellow-400 text-sm">★</span>
                </div>
                <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      stars >= 4 ? "bg-emerald-500" : stars === 3 ? "bg-amber-400" : "bg-rose-400"
                    }`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <div className="w-16 text-right font-semibold text-gray-500">
                  {count} <span className="text-[10px] text-gray-400">({pct}%)</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Card 3: Review Engagement & Quick Tips */}
        <div className="bg-gradient-to-br from-[#1b2d24] to-[#284033] rounded-3xl p-6 text-white shadow-sm flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xl">💬</span>
              <h3 className="text-sm font-black text-white uppercase tracking-wider">
                Customer Satisfaction
              </h3>
            </div>
            <p className="text-xs text-gray-300 leading-relaxed font-medium">
              Customers can leave a 1–5 star rating and written feedback immediately after their grooming or hotel service is marked complete.
            </p>
          </div>
          <div className="bg-white/10 rounded-2xl p-4 border border-white/10 space-y-1.5 mt-4">
            <div className="flex justify-between items-center text-xs">
              <span className="text-gray-300">Completed Services:</span>
              <span className="font-bold text-white">{completedAppointments.length}</span>
            </div>
            <div className="flex justify-between items-center text-xs">
              <span className="text-gray-300">Reviewed Appointments:</span>
              <span className="font-bold text-emerald-400">{stats.total}</span>
            </div>
            <div className="flex justify-between items-center text-xs">
              <span className="text-gray-300">Awaiting Customer Review:</span>
              <span className="font-bold text-amber-300">
                {Math.max(0, completedAppointments.length - stats.total)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Filters & Search Toolbar ── */}
      <div className="bg-white rounded-3xl border border-[#e1ebf0] p-4 sm:p-5 shadow-sm space-y-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          {/* Search box */}
          <div className="relative flex-1 max-w-md">
            <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400 text-sm">
              🔍
            </span>
            <input
              type="text"
              placeholder="Search by customer, pet, or review content..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs sm:text-sm font-medium text-gray-800 placeholder-gray-400 outline-none focus:border-[#6992a5] focus:bg-white transition-all"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 text-xs font-bold"
              >
                ✕
              </button>
            )}
          </div>

          {/* Service & Sort selects */}
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={serviceFilter}
              onChange={(e) => setServiceFilter(e.target.value)}
              className="px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs font-bold text-gray-700 outline-none focus:border-[#6992a5]"
            >
              <option value="all">All Services</option>
              <option value="grooming">Grooming Only</option>
              <option value="hotel">Hotel Only</option>
              <option value="both">Hotel &amp; Grooming</option>
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs font-bold text-gray-700 outline-none focus:border-[#6992a5]"
            >
              <option value="newest">Sort: Newest First</option>
              <option value="oldest">Sort: Oldest First</option>
              <option value="highest">Sort: Highest Rating</option>
              <option value="lowest">Sort: Lowest Rating</option>
            </select>
          </div>
        </div>

        {/* Star Filter Pill Buttons */}
        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-100">
          <span className="text-xs font-bold text-gray-400 uppercase tracking-wider mr-1">
            Star Rating:
          </span>
          {[
            { id: "all", label: "All Ratings" },
            { id: "5", label: "5 Stars ★★★★★" },
            { id: "4", label: "4 Stars ★★★★☆" },
            { id: "3", label: "3 Stars ★★★☆☆" },
            { id: "2", label: "2 Stars ★★☆☆☆" },
            { id: "1", label: "1 Star ★☆☆☆☆" },
          ].map((pill) => (
            <button
              key={pill.id}
              onClick={() => setStarFilter(pill.id)}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                starFilter === pill.id
                  ? "bg-amber-500 text-white shadow-sm"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-600"
              }`}
            >
              {pill.label}
              {pill.id !== "all" && (
                <span className="ml-1.5 opacity-75">
                  ({stats.distribution[pill.id] || 0})
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ── Reviews List ── */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base sm:text-lg font-black text-gray-800 flex items-center gap-2">
            <span>📝</span> Customer Reviews ({filteredReviews.length})
          </h3>
          {(searchTerm || starFilter !== "all" || serviceFilter !== "all") && (
            <button
              onClick={() => {
                setSearchTerm("");
                setStarFilter("all");
                setServiceFilter("all");
              }}
              className="text-xs font-bold text-[#6992a5] hover:underline"
            >
              Reset Filters
            </button>
          )}
        </div>

        {filteredReviews.length === 0 ? (
          <div className="bg-white rounded-3xl border border-[#e1ebf0] p-12 text-center space-y-3">
            <span className="text-5xl block mb-2">⭐</span>
            <h4 className="text-lg font-bold text-gray-700">No Reviews Found</h4>
            <p className="text-xs sm:text-sm text-gray-400 max-w-md mx-auto">
              {allReviews.length === 0
                ? "No completed appointments have received customer reviews yet. Once a service is completed, customers can rate their experience in their customer dashboard!"
                : "No customer reviews match your active filter criteria. Try clearing filters or search terms."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredReviews.map((apt) => {
              const reviewDate = apt.reviewedAt || apt.updatedAt || apt.createdAt;
              const formattedDate = reviewDate
                ? new Date(reviewDate).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })
                : "Recent";

              return (
                <div
                  key={apt._id}
                  className="bg-white rounded-3xl border-2 border-gray-100 hover:border-amber-200 p-5 sm:p-6 shadow-sm hover:shadow-md transition-all flex flex-col justify-between space-y-4 relative group"
                >
                  <div className="space-y-3">
                    {/* Header: Customer info & Star rating */}
                    <div className="flex items-start justify-between gap-3 border-b border-gray-100 pb-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#6992a5] to-[#46735c] text-white flex items-center justify-center font-black text-xs">
                            {apt.ownerName?.charAt(0) || "?"}
                          </div>
                          <div>
                            <h4 className="font-bold text-gray-800 text-sm leading-tight">
                              {apt.ownerName}
                            </h4>
                            <p className="text-[11px] text-gray-400">
                              {apt.ownerEmail || apt.ownerPhone}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="text-right shrink-0">
                        <div className="flex items-center gap-1.5 justify-end">
                          <StarRating rating={Number(apt.rating || 5)} size="text-lg" />
                          <span className="text-xs font-black text-amber-800 bg-amber-100 px-2 py-0.5 rounded-full">
                            {apt.rating}/5
                          </span>
                        </div>
                        <span className="text-[10px] text-gray-400 font-semibold block mt-0.5">
                          {formattedDate}
                        </span>
                      </div>
                    </div>

                    {/* Pets & Service tags */}
                    <div className="flex flex-wrap items-center gap-1.5">
                      {(apt.pets || []).map((pet, pIdx) => (
                        <span
                          key={pIdx}
                          className="px-2.5 py-1 bg-purple-50 text-purple-800 rounded-lg text-xs font-bold flex items-center gap-1 border border-purple-100"
                        >
                          <span>{pet.petType === "cat" ? "🐱" : "🐶"}</span>
                          <span>{pet.petName}</span>
                          <span className="text-gray-400 font-normal">
                            ({pet.serviceType === "both" ? "Hotel & Grooming" : pet.serviceType})
                          </span>
                        </span>
                      ))}
                    </div>

                    {/* Customer Review Feedback Quote */}
                    <div className="bg-amber-50/60 rounded-2xl p-4 border border-amber-200/80">
                      {apt.feedback ? (
                        <p className="text-gray-800 text-xs sm:text-sm italic leading-relaxed">
                          "{apt.feedback}"
                        </p>
                      ) : (
                        <p className="text-gray-400 text-xs italic font-medium">
                          Customer submitted a {apt.rating}-star rating with no written text.
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Footer: Appointment total and View Details action */}
                  <div className="flex items-center justify-between pt-3 border-t border-gray-100 text-xs">
                    <span className="font-semibold text-gray-500">
                      Total: <strong className="text-gray-800">₱{(apt.totalPrice || 0).toLocaleString()}</strong>
                    </span>
                    {onOpenDetails && (
                      <button
                        onClick={() => onOpenDetails(apt)}
                        className="px-3.5 py-1.5 bg-[#6992a5] hover:bg-[#5b7b88] text-white font-bold rounded-xl transition-colors shadow-2xs"
                      >
                        View Appointment &rarr;
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminReviews;
